import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Pricing.css';

const Pricing = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState('commercial');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = 'commercial';
      
      sections.forEach((section) => {
        if (window.scrollY >= section.offsetTop - 160) {
          currentSection = section.id;
        }
      });
      
      setActiveAnchor(currentSection);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleAnchorClick = (e, id) => {
    e.preventDefault();
    const section = document.querySelector(`#${id}`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const faqs = [
    {
      q: 'How much does it cost to list a cow or buffalo in the weekly commercial sale?',
      a: 'In the Weekly Commercial / Katoo Mall Sale, listing a heifer, cow, buffalo, or bull costs ₨2,500 per head. Semen straws cost ₨1,500 per straw and embryos cost ₨3,000 each. All prices are exclusive of GST and apply to a single person or vendor agent.'
    },
    {
      q: 'What is the difference between the Commercial Sale and the Breeder/Qurbani Sale fees?',
      a: 'The Breeder/Stud/Qurbani Sale is a premium category for higher-quality animals. Cattle and buffalo listing fees are ₨4,000 per head (vs ₨2,500 commercial), and sheep/goat fees are ₨2,500 per head (vs ₨1,500 commercial). Semen and embryo fees remain the same across both sale types.'
    },
    {
      q: 'What is included in the ₨30,000 Private Sale fee?',
      a: 'The ₨30,000 covers a complete single-day auction with invite-only access for up to 30 verified bidders. Included are the secure private bidding environment, private negotiation and counter-offer system, optional video previews, and trust-verified participant management (ID, bank and tax verified).'
    },
    {
      q: 'Is the 0.45% charge on machinery and property applied to the full sale price?',
      a: 'No. The 0.45% is applied only to the portion of the sale price above ₨1,000,000. For example, if a lot sells for ₨1,500,000, the 0.45% applies only to ₨500,000. This is in addition to the base listing fee of ₨15,000 for premium machinery and property lots.'
    },
    {
      q: 'Do I pay the listing fee if my lot does not sell?',
      a: 'The listing fee is charged when your lot is submitted and approved for auction. If your lot does not sell (passed in), you can relist it in the next available auction at no additional listing fee. Featured Lot marketing fees apply once the lot has been successfully featured.'
    }
  ];

  const CategoryCard = ({ emoji, title, items }) => (
    <div className="cat-card">
      <div className="cat-card-head">
        <span className="cat-emoji">{emoji}</span>
        <h3>{title}</h3>
      </div>
      <div className="cat-card-body">
        {items.map((item, idx) => (
          <div className="price-row" key={idx}>
            <span className="price-label">{item.label}</span>
            <span className="price-val">
              {item.price} <span className="price-unit">{item.unit}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const SingleCard = ({ tier, emoji, title, mainPrice, priceUnit, priceNote }) => (
    <div className="single-card">
      <div className={`single-card-tier tier-${tier}`}>
        {tier === 'premium' ? 'PREMIUM' : 'GENERAL'}
      </div>
      <div className="single-card-emoji">{emoji}</div>
      <h4>{title}</h4>
      <div className="main-price">{mainPrice}</div>
      <div className="price-per">{priceUnit}</div>
      {priceNote && <div className="price-note" dangerouslySetInnerHTML={{ __html: priceNote }} />}
    </div>
  );

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <header className="page-hero">
        <div className="page-hero-inner">
          <div className="hero-tag">2026 Fee Schedule</div>
          <h1>Transparent Pricing for <span>Every Auction Service</span></h1>
          <p>Clear, competitive pricing designed to help you succeed. All prices apply to a single person or vendor agent.</p>
          <div style={{ marginBottom: '10px' }}>
            <span className="gst-notice">⚠️ All prices are exclusive of GST</span>
          </div>
          <span className="effective-badge">📅 Effective from 1st January 2026</span>
        </div>
      </header>

      {/* Anchor Navigation */}
      <nav className="anchor-nav" aria-label="Pricing sections">
        <div className="anchor-nav-inner">
          {[
            { id: 'commercial', label: '🐄 Commercial Sale' },
            { id: 'breeder', label: '🏆 Breeder / Qurbani' },
            { id: 'private', label: '🔒 Private Sales' },
            { id: 'featured', label: '⭐ Featured Lots' },
            { id: 'horses', label: '🐎 Horses & Mules' },
            { id: 'machinery', label: '🚜 Machinery' },
            { id: 'feed', label: '🌾 Feed & Fodder' },
            { id: 'property', label: '🏡 Property' }
          ].map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`anchor-link ${activeAnchor === item.id ? 'active' : ''}`}
              onClick={(e) => handleAnchorClick(e, item.id)}
            >
              {item.label}
            </a>
          ))}
        </div>
      </nav>

      {/* 1. COMMERCIAL SALE */}
      <section className="sale-block" id="commercial">
        <div className="section-inner">
          <div className="sale-header">
            <div className="sale-type-badge badge-commercial">🐄 Weekly Sale</div>
            <h2 className="section-title">Weekly Commercial / Katoo Mall Sale</h2>
            <p className="section-sub">Regular weekly livestock sales open to all registered buyers across Pakistan and internationally.</p>
            <p className="sale-agent-note">* All prices are for a single person / vendor agent.</p>
          </div>
          <div className="category-grid">
            <CategoryCard
              emoji="🐄"
              title="Cattle / Buffalo"
              items={[
                { label: 'Heifers & Cow / Buffalo', price: '₨ 2,500', unit: '/ head' },
                { label: 'Bulls', price: '₨ 2,500', unit: '/ head' },
                { label: 'Semen Straws', price: '₨ 1,500', unit: '/ straw' },
                { label: 'Embryos', price: '₨ 3,000', unit: '/ embryo' }
              ]}
            />
            <CategoryCard
              emoji="🐑"
              title="Sheep / Goats"
              items={[
                { label: 'Ewes', price: '₨ 1,500', unit: '/ head' },
                { label: 'Rams', price: '₨ 1,500', unit: '/ head' },
                { label: 'Semen Straws', price: '₨ 1,500', unit: '/ straw' },
                { label: 'Embryos', price: '₨ 3,000', unit: '/ embryo' }
              ]}
            />
          </div>
        </div>
      </section>

      {/* 2. BREEDER / STUD / QURBANI SALE */}
      <section className="sale-block alt" id="breeder">
        <div className="section-inner">
          <div className="sale-header">
            <div className="sale-type-badge badge-breeder">🏆 Breeder Sale</div>
            <h2 className="section-title">Breeder / Stud / Qurbani Sale</h2>
            <p className="section-sub">Premium sale category for high-quality breeding stock, stud animals, and Qurbani-grade livestock.</p>
            <p className="sale-agent-note">* All prices are for a single person / vendor agent.</p>
          </div>
          <div className="category-grid">
            <CategoryCard
              emoji="🐄"
              title="Cattle / Buffalo"
              items={[
                { label: 'Heifers & Cow / Buffalo', price: '₨ 4,000', unit: '/ head' },
                { label: 'Bulls', price: '₨ 4,000', unit: '/ head' },
                { label: 'Semen Straws', price: '₨ 1,500', unit: '/ straw' },
                { label: 'Embryos', price: '₨ 3,000', unit: '/ embryo' }
              ]}
            />
            <CategoryCard
              emoji="🐑"
              title="Sheep / Goats"
              items={[
                { label: 'Ewes', price: '₨ 2,500', unit: '/ head' },
                { label: 'Rams', price: '₨ 2,500', unit: '/ head' },
                { label: 'Semen Straws', price: '₨ 1,500', unit: '/ straw' },
                { label: 'Embryos', price: '₨ 3,000', unit: '/ embryo' }
              ]}
            />
          </div>
        </div>
      </section>

      {/* 3. PRIVATE SALES */}
      <section className="sale-block" id="private">
        <div className="section-inner">
          <div className="sale-header">
            <div className="sale-type-badge badge-private">🔒 Invite Only</div>
            <h2 className="section-title">Special Private Sales</h2>
            <p className="section-sub">Any auction not part of a regularly scheduled Maveshi Bids sale. Based on a single-day sale.</p>
          </div>
          <div className="private-sale-grid">
            <div>
              <div className="perfect-for-label">Perfect for:</div>
              <div className="perfect-for-tags">
                <span className="pf-tag">High-value livestock</span>
                <span className="pf-tag">Breeding stock</span>
                <span className="pf-tag">Custom sales</span>
                <span className="pf-tag">Trusted buyer networks</span>
              </div>
              <ul className="private-features">
                <li><span className="feat-check">✓</span> Invite-only bidding for trusted networks</li>
                <li><span className="feat-check">✓</span> 30 verified, trust-screened bidders maximum</li>
                <li><span className="feat-check">✓</span> Private negotiation and counter-offer system</li>
                <li><span className="feat-check">✓</span> 24-hour auction window</li>
                <li><span className="feat-check">✓</span> Optional video lot previews</li>
                <li><span className="feat-check">✓</span> Trust-verified participants (ID, tax, bank verified)</li>
              </ul>
            </div>
            <div className="private-plan-card">
              <div className="plan-card-inner">
                <div className="plan-card-label">Complete Package</div>
                <div className="plan-card-title">Private Sale Plan</div>
                <table className="plan-table">
                  <thead>
                    <tr>
                      <th>Up to 30 Bidders</th>
                      <th></th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Auction Duration</td>
                      <td>Single Day</td>
                      <td className="plan-price-cell">—</td>
                    </tr>
                    <tr>
                      <td>Bidder Limit</td>
                      <td>Verified</td>
                      <td className="plan-price-cell">—</td>
                    </tr>
                    <tr>
                      <td>Negotiation</td>
                      <td>Full Access</td>
                      <td className="plan-price-cell">—</td>
                    </tr>
                    <tr>
                      <td><strong>Total</strong></td>
                      <td><strong>1 Day</strong></td>
                      <td className="plan-price-cell">
                        <div className="plan-price-big">₨30,000</div>
                        <div className="plan-price-note">excl. GST</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button className="plan-cta" onClick={() => navigate('/register')}>Register & Create → </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FEATURED LOTS */}
      <section className="featured-section" id="featured">
        <div className="section-inner">
          <div>
            <div className="sale-type-badge badge-featured">⭐ Featured Lots</div>
            <h2 className="section-title">Featured Lots — Greater Exposure</h2>
            <p className="section-sub">Nominate a lot as a Featured Lot to gain increased visibility in dedicated Featured Lot carousels across the Maveshi Bids website.</p>
          </div>
          <div className="featured-card">
            <div>
              <div className="featured-price-label">Marketing Fee per Nomination</div>
              <div className="featured-price-big">₨15,000</div>
              <div className="featured-price-unit">Charged as Marketing Fee</div>
              <button className="feat-cta-btn" onClick={() => navigate('/register')}>Feature Your Lot →</button>
            </div>
            <div className="featured-rules">
              <div className="featured-rule">
                <div className="rule-dot"></div>
                <strong>Increased Visibility:</strong> Featured Lots appear in premium carousels across the Maveshi Bids website, increasing buyer engagement.
              </div>
              <div className="featured-rule">
                <div className="rule-dot"></div>
                <strong>Per Nomination:</strong> Each Featured Lot nomination is charged separately. Re-offered lots require a new nomination.
              </div>
              <div className="featured-rule">
                <div className="rule-dot"></div>
                <strong>Marketing Advantage:</strong> Boost your lot visibility to thousands of active buyers with a dedicated featured placement.
              </div>
              <div className="featured-rule">
                <div className="rule-dot"></div>
                <strong>Sale Category Agnostic:</strong> Available for any sale type—Commercial, Breeder, Private, or Horses auctions.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. HORSES / MULES */}
      <section className="sale-block alt" id="horses">
        <div className="section-inner">
          <div>
            <div className="sale-type-badge badge-horses">🐎 Equine</div>
            <h2 className="section-title">Horses / Mules</h2>
            <p className="section-sub">Listing fees for equine lots in scheduled Maveshi Bids auctions.</p>
          </div>
          <div className="single-grid">
            <SingleCard
              tier="premium"
              emoji="🐎"
              title="Premium Horses"
              mainPrice="₨ 10,000"
              priceUnit="per head"
              priceNote="High-breed registered horses, racing stock, polo ponies, and superior bloodline equines."
            />
            <SingleCard
              tier="general"
              emoji="🐴"
              title="General Category"
              mainPrice="₨ 5,000"
              priceUnit="per head"
              priceNote="Mules, donkeys, and general category horses."
            />
          </div>
        </div>
      </section>

      {/* 6. MACHINERY & EQUIPMENT */}
      <section className="sale-block" id="machinery">
        <div className="section-inner">
          <div>
            <div className="sale-type-badge badge-machinery">🚜 Machinery</div>
            <h2 className="section-title">Machinery & Equipment</h2>
            <p className="section-sub">Listing fees for farm machinery, industrial equipment, and agricultural tools.</p>
          </div>
          <div className="single-grid">
            <SingleCard
              tier="premium"
              emoji="🚜"
              title="Premium Machinery"
              mainPrice="₨ 15,000"
              priceUnit="per lot"
              priceNote="<strong>Plus 0.45%</strong> of sale price on amounts exceeding ₨1,000,000. For example: ₨1,500,000 sale = ₨15,000 base + ₨2,250 (0.45% of ₨500,000) = ₨17,250 total (excl. GST)"
            />
            <SingleCard
              tier="general"
              emoji="⚙️"
              title="General Machinery"
              mainPrice="₨ 8,000"
              priceUnit="per lot"
              priceNote="Standard farm equipment, tools, and general agricultural machinery."
            />
          </div>
        </div>
      </section>

      {/* 7. FEED & FODDER */}
      <section className="sale-block alt" id="feed">
        <div className="section-inner">
          <div>
            <div className="sale-type-badge badge-feed">🌾 Feed & Fodder</div>
            <h2 className="section-title">Feed & Fodder Sales</h2>
            <p className="section-sub">Listing fees for animal feed, fodder, and agricultural produce sold through Maveshi Bids.</p>
          </div>
          <div className="single-grid">
            <SingleCard
              tier="premium"
              emoji="🌾"
              title="Premium Feed Lots"
              mainPrice="₨ 8,000"
              priceUnit="per lot"
              priceNote="High-quality branded feeds, specialty feed blends, and bulk quantities."
            />
            <SingleCard
              tier="general"
              emoji="🌱"
              title="General Feed & Fodder"
              mainPrice="₨ 3,000"
              priceUnit="per lot"
              priceNote="Standard hay, chaff, straw, general feed, and agricultural byproducts."
            />
          </div>
        </div>
      </section>

      {/* 8. PROPERTY SALES */}
      <section className="sale-block" id="property">
        <div className="section-inner">
          <div>
            <div className="sale-type-badge badge-property">🏡 Property</div>
            <h2 className="section-title">Property Sales</h2>
            <p className="section-sub">Agricultural land, farms, orchards, and rural properties sold through transparent online auction.</p>
          </div>
          <div className="property-card">
            <div className="property-left">
              <h3>Agricultural Land & Farm Properties</h3>
              <p>Secure and transparent auction of agricultural land, farms, orchards, pastures, and rural properties with full verification and legal documentation support.</p>
              <button className="prop-cta" onClick={() => navigate('/register')}>List Your Property →</button>
            </div>
            <div className="property-price-wrap">
              <div className="property-price-flat">₨ 50,000</div>
              <div className="property-price-label">Listing Fee</div>
              <div className="property-plus">+ 0.45% on sale price above ₨1,000,000 (excl. GST)</div>
            </div>
          </div>
        </div>
      </section>

      {/* ADDITIONAL INFORMATION */}
      <section className="additional-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">Additional Information</span>
            <h2 className="section-title">2026 Listing Fees — Key Notes</h2>
          </div>
          <div className="info-grid">
            <div className="info-card">
              <h4>📋 Fee Structure</h4>
              <ul>
                <li>All listing fees are per single person or vendor agent</li>
                <li>Additional sellers/co-vendors incur separate fees</li>
                <li>Video preview and optional lot services are separate</li>
              </ul>
            </div>
            <div className="info-card">
              <h4>🛡️ Trust & Verification</h4>
              <ul>
                <li>All buyers ID, tax, and bank verified</li>
                <li>Maveshi Bids arbitration for disputes</li>
                <li>Secure payment processing integration</li>
              </ul>
            </div>
            <div className="info-card">
              <h4>🔄 Re-listing & Relisting</h4>
              <ul>
                <li>Unsold lots relist free in next available auction</li>
                <li>Featured Lot fees apply per nomination</li>
                <li>No fee rollover between sales</li>
              </ul>
            </div>
            <div className="info-card">
              <h4>💳 Payment & GST</h4>
              <ul>
                <li>All prices exclusive of GST (17%)</li>
                <li>Digital payment options available</li>
                <li>Invoices provided for tax purposes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">Pricing FAQs</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFAQ === index ? 'open' : ''}`}
              >
                <div
                  className="faq-q"
                  tabIndex="0"
                  onClick={() => toggleFAQ(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleFAQ(index);
                    }
                  }}
                  role="button"
                >
                  {faq.q}
                  <div className="faq-arrow">▾</div>
                </div>
                <p className="faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="cta-strip">
        <div>
          <h2>Ready to List Your First Lot?</h2>
          <p>Register free, choose your sale type, and have your listing live within 24 hours. Questions? Our team is on WhatsApp.</p>
          <div className="cta-btns">
            <button className="btn-primary" onClick={() => navigate('/register')}>Start Listing Free →</button>
            <button className="btn-outline" onClick={() => navigate('/contact')}>Contact Us</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
